import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlusIcon } from 'lucide-react';
import type { Faq } from '../data/faqs';
type ServiceFaqProps = {
  serviceName: string;
  faqs: Faq[];
  showContact?: boolean;
};
const PLAN_NAMES = /(Launch|Co-Pilot|Autopilot|Pilot)/g;
export function ServiceFaq({
  serviceName,
  faqs,
  showContact = true
}: ServiceFaqProps) {
  const [open, setOpen] = useState<number | null>(null);
  const id = `faq-${serviceName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <section aria-labelledby={`${id}-title`} className="bg-white px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-id="element-766" data-mp="1bkuvd1lh56d3">
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(schema)
    }} data-id="element-767" data-mp="j2pi7g2i5tzr" />
      <div className="mx-auto max-w-[800px]" data-id="element-768" data-mp="1hir2g1rgw91q">
        <h2 id={`${id}-title`} className="text-3xl font-extrabold tracking-tight text-brand-black sm:text-4xl" data-id="element-769" data-mp="1hhfppcg1nqyh">
          {serviceName} — Frequently Asked Questions
        </h2>

        <div className="mt-10 divide-y divide-gray-200 border-y border-gray-200" data-id="element-770" data-mp="17s5xlwl7a8ad">
          {faqs.map((faq, i) => {
          const isOpen = open === i;
          return <div key={faq.q} data-id="element-771" data-mp="xui2h5jomyro" data-mpi={i}>
                <h3 data-id="element-772" data-mp="xvt7f8ov6ckd">
                  <button type="button" id={`${id}-q-${i}`} aria-expanded={isOpen} aria-controls={`${id}-a-${i}`} onClick={() => setOpen(isOpen ? null : i)} className="flex min-h-[64px] w-full items-center justify-between gap-6 py-5 text-left text-lg font-bold text-brand-black transition-colors duration-150 ease-out hover:text-brand-red focus:outline-none focus-visible:text-brand-red" data-id="element-773" data-mp="xw2w8sy6adoh">
                    {faq.q}
                    <PlusIcon className={`h-5 w-5 flex-shrink-0 text-brand-red transition-transform duration-200 ease-out ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" data-id="element-774" data-mp="cjas5ormcahj" />
                  </button>
                </h3>
                <AnimatePresence initial={false} data-id="element-775" data-mp="7khkicz0jdpa">
                  {isOpen && <motion.div id={`${id}-a-${i}`} role="region" aria-labelledby={`${id}-q-${i}`} initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="overflow-hidden" data-id="element-776" data-mp="7iepfej06udp">
                      <p className="pb-6 pr-10 text-base leading-relaxed text-gray-600" data-id="element-777" data-mp="364ptke5iawp7">{renderAnswer(faq.a)}</p>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>

        {showContact && <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-778" data-mp="1o4i7m9wnx34q">
            <p className="text-lg font-bold text-brand-black" data-id="element-779" data-mp="2226rpfiwo68q">Still have questions?</p>
            <a href="#/contact" className="w-full rounded-md bg-brand-red px-6 py-3 text-center text-sm font-semibold text-white shadow-sm transition-colors duration-150 ease-out hover:bg-brand-red-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2 sm:w-auto" data-id="element-780" data-mp="1y0tjwzw95sz6">
              CONTACT US
            </a>
          </div>}
      </div>
    </section>;
}
function renderAnswer(text: string) {
  return text.split(PLAN_NAMES).map((part, i) => i % 2 === 1 ? <strong key={i} className="font-bold text-brand-black" data-id="element-781" data-mp="mokah6x0xuxo">
        {part}
      </strong> : <React.Fragment key={i} data-id="element-782" data-mp="ze1hygtlofvm">{part}</React.Fragment>);
}